import { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { LogOut, RefreshCw, Volume2, VolumeX, MoreHorizontal, ChevronLeft, ChevronDown } from 'lucide-react'

import audioManager from '../../services/audioManager'
import { getExperienceForLevel, getExperienceToNextLevel, getProgressToNextLevel } from '../../utils/progression'
import { PINNED_ITEM, NAV_GROUPS, getUserItems, isPathActive, resolveBadge, visibleNavGroups } from './navConfig'
import SidebarItem from './SidebarItem'

const SECTION_STORAGE_KEY = 'sidebar.collapsedSections'

const loadCollapsedSections = () => {
    try {
        return JSON.parse(localStorage.getItem(SECTION_STORAGE_KEY)) || {}
    } catch {
        return {}
    }
}

// Nav section with a collapsible header (persisted). Renders as a plain icon
// stack when the sidebar is collapsed to the rail.
const NavSection = ({ label, items, pathname, collapsed, isOpen, onToggle, badges }) => {
    if (collapsed) {
        return (
            <div className="flex flex-col items-center gap-1.5">
                {items.map(item => (
                    <SidebarItem
                        key={item.path}
                        {...item}
                        badge={resolveBadge(item, badges)}
                        isActive={isPathActive(item.path, pathname)}
                        isCollapsed
                    />
                ))}
            </div>
        )
    }

    return (
        <div>
            <button
                onClick={onToggle}
                className="mb-1.5 flex w-full items-center justify-between px-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted transition-colors hover:text-text-light"
                aria-expanded={isOpen}
            >
                <span>{label}</span>
                <ChevronDown size={12} className={`transition-transform duration-150 ${isOpen ? '' : '-rotate-90'}`} />
            </button>
            {isOpen && (
                <div className="flex flex-col gap-0.5">
                    {items.map(item => (
                        <SidebarItem
                            key={item.path}
                            {...item}
                            badge={resolveBadge(item, badges)}
                            isActive={isPathActive(item.path, pathname)}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}

const Sidebar = ({
    currentUser,
    demoModeEnabled,
    onResetCharacter,
    onLogout,
    isCollapsed,
    onToggleCollapse,
    playerMeta,
}) => {
    const location = useLocation()
    const { badges, globalLevel, globalTotalExperience } = playerMeta

    const [soundEnabled, setSoundEnabled] = useState(audioManager.isEnabled())
    const [menuOpen, setMenuOpen] = useState(false)
    const [collapsedSections, setCollapsedSections] = useState(loadCollapsedSections)
    const sidebarRef = useRef(null)
    const navRef = useRef(null)
    const [navOverflow, setNavOverflow] = useState({ top: false, bottom: false })

    // H6: the nav outgrows short laptop viewports, so surface a scroll affordance
    // (top/bottom fade + jump chevron) whenever it actually overflows.
    useEffect(() => {
        const el = navRef.current
        if (!el) return

        const update = () => {
            const { scrollTop, scrollHeight, clientHeight } = el
            setNavOverflow({
                top: scrollTop > 4,
                bottom: scrollTop + clientHeight < scrollHeight - 4,
            })
        }

        update()
        el.addEventListener('scroll', update, { passive: true })
        const observer = new ResizeObserver(update)
        observer.observe(el)
        Array.from(el.children).forEach(child => observer.observe(child))
        window.addEventListener('resize', update)

        return () => {
            el.removeEventListener('scroll', update)
            observer.disconnect()
            window.removeEventListener('resize', update)
        }
    }, [collapsedSections])

    const scrollNavDown = () => {
        navRef.current?.scrollBy({ top: 140, behavior: 'smooth' })
    }

    useEffect(() => {
        // Default the rail on compact laptop widths; user can still expand.
        if (window.innerWidth < 1280 && !isCollapsed) {
            onToggleCollapse()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Collapse an expanded sidebar when clicking anywhere outside of it.
    useEffect(() => {
        if (isCollapsed) return
        const handlePointerDown = (e) => {
            if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
                onToggleCollapse()
            }
        }
        document.addEventListener('pointerdown', handlePointerDown)
        return () => document.removeEventListener('pointerdown', handlePointerDown)
    }, [isCollapsed, onToggleCollapse])

    const toggleSection = (key) => {
        setCollapsedSections(prev => {
            const next = { ...prev, [key]: !prev[key] }
            try {
                localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(next))
            } catch {
                /* storage unavailable — non-critical */
            }
            return next
        })
    }

    const handleSoundToggle = () => {
        setSoundEnabled(audioManager.toggle())
    }

    const collapsed = isCollapsed
    const userItems = getUserItems({ demoModeEnabled, handle: currentUser?.handle })
    const navGroups = visibleNavGroups(NAV_GROUPS, currentUser?.is_admin)
    const xpProgress = getProgressToNextLevel(globalTotalExperience)
    const hasPendingReward = badges.achievements > 0 || badges.market || badges.daily

    const renderNavList = (rail) => (
        <>
            <div className={rail ? 'flex justify-center' : 'mb-5'}>
                <SidebarItem
                    {...PINNED_ITEM}
                    badge={resolveBadge(PINNED_ITEM, badges)}
                    isActive={isPathActive(PINNED_ITEM.path, location.pathname)}
                    isCollapsed={rail}
                />
            </div>
            {navGroups.map(group => (
                <NavSection
                    key={group.key}
                    label={group.label}
                    items={group.items}
                    pathname={location.pathname}
                    collapsed={rail}
                    isOpen={!collapsedSections[group.key]}
                    onToggle={() => toggleSection(group.key)}
                    badges={badges}
                />
            ))}
            <div className={rail ? 'mt-5 flex flex-col items-center gap-1.5' : 'mt-5'}>
                {!rail && (
                    <div className="mb-1.5 px-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                        Account
                    </div>
                )}
                {userItems.map(item => (
                    <SidebarItem
                        key={item.path}
                        {...item}
                        badge={resolveBadge(item, badges)}
                        isActive={isPathActive(item.path, location.pathname)}
                        isCollapsed={rail}
                    />
                ))}
            </div>
        </>
    )

    // Shared footer controls (sound / reset / logout) in an overflow popover,
    // so utilities never sit in the navigation list.
    const renderOverflowMenu = (rail) => (
        <div className="relative">
            <button
                onClick={() => setMenuOpen(open => !open)}
                className={`flex items-center justify-center rounded-lg p-1.5 text-text-muted transition-colors hover:bg-bg-hover hover:text-text-light ${rail ? '' : 'flex-shrink-0'}`}
                aria-label="More options"
            >
                <MoreHorizontal size={18} />
            </button>
            {menuOpen && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                    <div className={`absolute bottom-full z-50 mb-2 w-44 rounded-lg border border-border-light bg-bg-elevated py-1 shadow-lg ${rail ? 'left-full ml-2' : 'right-0'}`}>
                        <button
                            onClick={handleSoundToggle}
                            className="flex w-full items-center gap-2.5 px-2 py-1.5 text-sm text-text-light transition-colors hover:bg-bg-hover"
                        >
                            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                            {soundEnabled ? 'Sound On' : 'Sound Off'}
                        </button>
                        {demoModeEnabled && (
                            <button
                                onClick={() => { setMenuOpen(false); onResetCharacter() }}
                                className="flex w-full items-center gap-2.5 px-2 py-1.5 text-sm text-text-light transition-colors hover:bg-bg-hover hover:text-accent-red"
                            >
                                <RefreshCw size={16} />
                                Reset Character
                            </button>
                        )}
                        <button
                            onClick={() => { setMenuOpen(false); onLogout() }}
                            className="flex w-full items-center gap-2.5 px-2 py-1.5 text-sm text-text-light transition-colors hover:bg-bg-hover"
                        >
                            <LogOut size={16} />
                            Logout
                        </button>
                    </div>
                </>
            )}
        </div>
    )

    // XP progress ring around the avatar — doubles as the level indicator.
    const renderLevelAvatar = (size) => (
        <div className="group relative flex-shrink-0" style={{ width: size, height: size }}>
            <svg className="absolute inset-0 h-full w-full -rotate-90">
                <circle cx={size / 2} cy={size / 2} r={size / 2 - 2} fill="transparent" stroke="currentColor" strokeWidth="2" className="text-accent-blue opacity-20" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={size / 2 - 2}
                    fill="transparent"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeDasharray={2 * Math.PI * (size / 2 - 2)}
                    strokeDashoffset={(2 * Math.PI * (size / 2 - 2)) * (1 - xpProgress / 100)}
                    strokeLinecap="round"
                    className="text-accent-blue transition-all duration-500 ease-out"
                />
            </svg>
            <div className="absolute inset-[4px] flex items-center justify-center rounded-full bg-bg-panel text-xs font-bold text-accent-blue">
                {globalLevel}
            </div>
        </div>
    )

    return (
        <aside
            ref={sidebarRef}
            className="fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-border-light bg-bg-darkest w-[var(--sidebar-w)] transition-[width] duration-200 ease-out lg:flex"
        >
            {/* Header: logo + wordmark, collapse toggle */}
            <div className={`border-b border-border-light ${collapsed ? 'flex flex-col items-center gap-2 px-2 py-2' : 'flex h-16 items-center gap-2 px-3'}`}>
                <Link to="/shelter" className="flex items-center gap-2 transition-opacity hover:opacity-80">
                    <div className="relative h-9 w-9 flex-shrink-0">
                        <img src="/logo-mark.png" alt="Commonfolk Downfall" className="h-full w-full object-contain" />
                        {hasPendingReward && (
                            <div className="absolute -right-1 -top-1 h-2.5 w-2.5 animate-pulse rounded-full border-2 border-bg-darkest bg-accent-red" />
                        )}
                    </div>
                    {!collapsed && (
                        <div className="flex min-w-0 flex-col leading-tight">
                            <span className="truncate text-base font-bold tracking-tight text-white">Commonfolk</span>
                            <span className="text-[10px] font-medium uppercase tracking-wider text-text-muted">Downfall</span>
                        </div>
                    )}
                </Link>
                <button
                    onClick={onToggleCollapse}
                    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    className={`rounded-md p-1.5 text-text-muted transition-colors hover:bg-bg-hover hover:text-white ${collapsed ? '' : 'ml-auto'}`}
                >
                    <ChevronLeft size={16} className={`transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`} />
                </button>
            </div>

            {/* Scrollable navigation */}
            <div className="relative flex-1 min-h-0 flex flex-col">
                <nav
                    ref={navRef}
                    className={`flex-1 min-h-0 overflow-y-auto custom-scrollbar ${collapsed ? 'flex flex-col items-center gap-5 py-3' : 'space-y-5 px-2 py-3'}`}
                >
                    {renderNavList(collapsed)}
                </nav>

                {/* Scroll affordances: edge fades + jump chevron, only when the list overflows */}
                {navOverflow.top && (
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-bg-darkest to-transparent" />
                )}
                {navOverflow.bottom && (
                    <>
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-bg-darkest to-transparent" />
                        <button
                            type="button"
                            onClick={scrollNavDown}
                            data-nav-scroll-affordance="true"
                            aria-label="More navigation items below"
                            className={`absolute bottom-1 z-10 flex h-6 w-6 items-center justify-center rounded-full border border-border-light bg-bg-elevated text-text-muted shadow-lg transition-colors hover:text-white ${collapsed ? 'left-1/2 -translate-x-1/2' : 'right-2'}`}
                        >
                            <ChevronDown size={14} />
                        </button>
                    </>
                )}
            </div>

            {/* Footer: level avatar + XP, overflow utilities */}
            <div className={`border-t border-border-light ${collapsed ? 'flex flex-col items-center gap-2 py-2' : 'flex items-center gap-2 p-2'}`}>
                <div className="group relative">
                    {renderLevelAvatar(collapsed ? 44 : 36)}
                    {/* XP tooltip (pure CSS hover) */}
                    <div className={`pointer-events-none absolute bottom-full z-50 mb-2 w-56 rounded-lg border border-accent-blue bg-bg-darker p-3 shadow-xl ${collapsed ? 'left-full ml-3' : 'left-0'} hidden group-hover:block`}>
                        <p className="mb-1 text-sm font-bold text-accent-yellow">Global Level {globalLevel}</p>
                        <p className="mb-1 text-xs text-text-lighter">
                            XP: <span className="text-white">{globalTotalExperience}</span> / {getExperienceForLevel(globalLevel + 1)}
                        </p>
                        <p className="text-xs text-accent-green">Next Lvl: {getExperienceToNextLevel(globalTotalExperience)} XP</p>
                    </div>
                </div>
                {!collapsed && (
                    <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between">
                            <span className="truncate text-xs font-semibold text-white">
                                {currentUser?.handle ? `@${currentUser.handle}` : 'Player'}
                            </span>
                            <span className="font-mono text-[10px] text-accent-blue">{xpProgress}%</span>
                        </div>
                        <div className="mt-1 h-1 overflow-hidden rounded-full bg-bg-card">
                            <div
                                className="h-full rounded-full bg-gradient-to-r from-accent-blue to-accent-green transition-all duration-500"
                                style={{ width: `${xpProgress}%` }}
                            />
                        </div>
                        <span className="text-[10px] text-text-muted">LVL {globalLevel}</span>
                    </div>
                )}
                {renderOverflowMenu(collapsed)}
            </div>
        </aside>
    )
}

export default Sidebar
